package fr.manyverse.app.presentation.components.modules.timer

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveTimer
import kotlinx.coroutines.delay
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.timer_pause
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock

private const val CRITICAL_TIME_IN_SECONDS = 30

/**
 * A single floating timer bubble showing a countdown.
 * Calls [onExpired] when the timer reaches zero. The countdown is frozen while the GM pauses it.
 */
@Composable
fun TimerBubble(
    timer: ActiveTimer,
    onExpired: () -> Unit,
    modifier: Modifier = Modifier
) {
    // Keyed on the whole timer so that a pause/resume received via Realtime recomputes the countdown
    var remainingSeconds by remember(timer) { mutableIntStateOf(timer.remainingSecondsAt(Clock.System.now())) }

    // Tick every second, frozen while paused
    LaunchedEffect(timer) {
        if (timer.isPaused && remainingSeconds > 0) return@LaunchedEffect
        while (remainingSeconds > 0) {
            delay(1000)
            remainingSeconds = timer.remainingSecondsAt(Clock.System.now())
        }
        onExpired()
    }

    // If already expired, don't show
    if (remainingSeconds <= 0) return

    val isUrgent = !timer.isPaused && remainingSeconds <= CRITICAL_TIME_IN_SECONDS

    val blinkAlpha = if (isUrgent) {
        val infiniteTransition = rememberInfiniteTransition()
        val animatedAlpha by infiniteTransition.animateFloat(
            initialValue = 1f,
            targetValue = 0.3f,
            animationSpec = infiniteRepeatable(
                animation = tween(500),
                repeatMode = RepeatMode.Reverse
            )
        )
        animatedAlpha
    } else 1f

    val textColor = if (isUrgent) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface

    Surface(
        modifier = modifier.graphicsLayer { alpha = blinkAlpha },
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
        shadowElevation = 4.dp
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = Icons.Default.Timer,
                contentDescription = null,
                tint = textColor,
                modifier = Modifier.size(24.dp)
            )
            Text(
                text = formatCountdown(remainingSeconds),
                style = MaterialTheme.typography.titleMedium,
                color = textColor
            )
            if (timer.isPaused) {
                Icon(
                    imageVector = Icons.Default.Pause,
                    contentDescription = stringResource(Res.string.timer_pause),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

